import type { ResultUpload } from 'ts-components/PiChat/protocol';
import { redactUrl, resolveUploadUrl } from 'ts-components/PiChat/uploadUrl';

export type Warn = (message: string) => void;

export const defaultWarn: Warn = (message) => {
  // eslint-disable-next-line no-console -- a refused or failed upload has no other trace on the client
  console.warn(message);
};

export type PutStatus = 'stored' | 'expired' | 'failed';

export const presignedPut = async (
  { url, headers }: { url: string; headers: Record<string, string> },
  body: Blob,
  label: string,
  signal: AbortSignal,
  fetchImpl: typeof fetch,
  warn: Warn
): Promise<PutStatus> => {
  try {
    const response = await fetchImpl(url, {
      method: 'PUT',
      headers: { ...headers },
      body,
      signal,
      credentials: 'omit',
      redirect: 'error',
    });
    if (response.ok || response.status === 412) return 'stored';
    warn(
      `${label} upload to ${redactUrl(url)} failed: HTTP ${response.status}`
    );
    return 'failed';
  } catch (error) {
    if (signal.aborted) {
      warn(`${label} upload expired before it finished`);
      return 'expired';
    }
    const name = error instanceof Error ? error.name : 'Error';
    warn(`${label} upload to ${redactUrl(url)} failed: ${name}`);
    return 'failed';
  }
};

export const resolveUploadUrls = (
  urls: string[],
  wsUrl: string,
  label: string,
  warn: Warn
): string[] | null => {
  const resolved = urls.map((url) => resolveUploadUrl(url, wsUrl));
  if (resolved.every((url): url is string => Boolean(url))) return resolved;
  const refused = urls.filter((_, i) => !resolved[i]).map(redactUrl);
  warn(`${label} refused: upload URL not allowed (${refused.join(', ')})`);
  return null;
};

export const presignedPutBeforeExpiry = async <T>(
  expiresAt: number,
  expiredMessage: string,
  warn: Warn,
  run: (signal: AbortSignal) => Promise<T>
): Promise<T | 'expired'> => {
  const remainingMs = expiresAt - Date.now();
  if (remainingMs <= 0) {
    warn(expiredMessage);
    return 'expired';
  }
  const expiry = new AbortController();
  const timer = setTimeout(() => expiry.abort(), remainingMs);
  try {
    return await run(expiry.signal);
  } finally {
    clearTimeout(timer);
  }
};

export const RESULT_DEADLINE_MARGIN_MS = 5_000;

export interface ResolvedResultUpload<R> {
  label: string;
  url: string;
  headers: Record<string, string>;
  maxBytes: number;
  expiresAt: number;
  /** A small failure, valid for this tool's result schema, sent when the real result exceeds `maxBytes`. */
  tooLarge: R;
}

export const resolveResultUpload = <R>(
  { result, expiresAt }: ResultUpload,
  wsUrl: string,
  label: string,
  tooLarge: R
): ResolvedResultUpload<R> | null => {
  const urls = resolveUploadUrls([result.url], wsUrl, label, defaultWarn);
  if (!urls) return null;
  const [url] = urls;
  return {
    label,
    url,
    headers: result.headers,
    maxBytes: result.maxBytes,
    expiresAt,
    tooLarge,
  };
};

/** The UTF-8 body for `result`, falling back to `upload.tooLarge`; null when neither fits `maxBytes`. */
export const encodeResultBody = <R>(
  upload: Pick<ResolvedResultUpload<R>, 'maxBytes' | 'tooLarge'>,
  result: R
): Uint8Array | null => {
  const encoder = new TextEncoder();
  return (
    [result, upload.tooLarge]
      .map((body) => encoder.encode(JSON.stringify(body)))
      .find((body) => body.length <= upload.maxBytes) ?? null
  );
};

export const sendResultUpload = async <R>(
  upload: ResolvedResultUpload<R>,
  result: R,
  fetchImpl: typeof fetch = (...args) => globalThis.fetch(...args)
): Promise<PutStatus | 'expired'> => {
  const body = encodeResultBody(upload, result);
  if (!body) {
    defaultWarn(
      `${upload.label} not answered: its result exceeds ${upload.maxBytes} bytes`
    );
    return 'failed';
  }
  return presignedPutBeforeExpiry(
    upload.expiresAt,
    `${upload.label} not answered: its upload URL has expired`,
    defaultWarn,
    (signal) =>
      presignedPut(
        upload,
        new Blob([body]),
        `${upload.label} result`,
        signal,
        fetchImpl,
        defaultWarn
      )
  );
};
